import React, { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { FaCalendarAlt, FaPhoneAlt } from "react-icons/fa"
import { FaRegCircleCheck, FaRegCircleXmark } from "react-icons/fa6"
import { IoDocumentText, IoTicket } from "react-icons/io5"
import { Route, useNavigate } from "react-router-dom"

import BottomSheet, {
  BottomSheetHandle,
} from "@fuse/sharedComponents/bottomSheet/BottomSheet"
import LoadingSpinnerInline from "@fuse/sharedComponents/loader/LoadingSpinnerInline"
import { EventStatusType, TEventDetail } from "@letshang/constants"
import { ROUTES } from "src/App"

import { useGuestListData } from "./useGuestListData"

enum ReviewGuestAction {
  APPROVE = "Approve",
  DISAPPROVE = "Disapprove",
}

export interface ReviewGuestsHandle {
  openModal: () => void
  closeModal: () => void
}

const ReviewGuests = forwardRef<
  ReviewGuestsHandle,
  {
    guest: any
    eventId: string
    handleApproveGuest: (guestDetails: any) => Promise<{ success: boolean }>
    handleDisapproveGuest: (guestDetails: any) => Promise<{ success: boolean }>
    onClose?: () => void
    handleCheckInGuest: (guestDetails: any) => Promise<{ success: boolean }>
    isTicketedEvent: boolean
    eventDetails: TEventDetail
    currentTab: string
  }
>(
  (
    {
      guest,
      eventId,
      onClose,
      handleApproveGuest,
      handleDisapproveGuest,
      handleCheckInGuest,
      isTicketedEvent,
      eventDetails,
      currentTab,
    },
    ref,
  ) => {
    const { disapproveApplicantCall, approveApplicantCall } =
      useGuestListData(eventId)
    const bottomSheetRef = useRef<BottomSheetHandle>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isCheckedIn, setIsCheckedIn] = useState(false)
    const [isGuestReviewed, setIsGuestReviewed] = useState(false)
    const [currentAction, setCurrentAction] = useState<ReviewGuestAction | "">(
      "",
    )

    const handleCloseModal = (internalClose: boolean = false) => {
      if (!internalClose) {
        bottomSheetRef.current?.closeModal()
      }
      setIsGuestReviewed(false)
      setIsCheckedIn(false)
      setCurrentAction("")
      setIsLoading(false)
      onClose?.()
    }

    useImperativeHandle(ref, () => ({
      openModal: () => {
        bottomSheetRef.current?.openModal()
      },
      closeModal: handleCloseModal,
    }))

    const handleReviewGuest = async (action: ReviewGuestAction) => {
      if (isLoading) return
      setIsLoading(true)

      if (action === ReviewGuestAction.APPROVE) {
        const res = await handleApproveGuest(guest)
        if (res?.success) {
          setIsGuestReviewed(true)
        }
      } else if (action === ReviewGuestAction.DISAPPROVE) {
        const res = await handleDisapproveGuest(guest)
        if (res?.success) {
          setIsGuestReviewed(true)
        }
      }
      // handleCloseModal()
      // setCurrentAction("")
      setIsLoading(false)
    }

    const checkInGuest = async () => {
      if (isLoading) return
      setIsLoading(true)
      const res = await handleCheckInGuest(guest)
      if (res?.success) {
        setIsCheckedIn(true)
      }
      // handleCloseModal()
      setIsLoading(false)
    }

    const navigate = useNavigate()

    const onReviewQuestionnaire = () => {
      navigate(ROUTES.REVIEW_QUESTIONNAIRE.replace(":eventId", eventId), {
        state: {
          answersToQuestionnaire: guest?.answersToQuestionnaire,
          eventDetails,
          currentTab,
        },
      })
    }

    return (
      <BottomSheet
        ref={bottomSheetRef}
        title="Review Guest"
        isDefaultBg={false}
        showHeader={false}
        // onOpen={handleOpen}
        // onOpen={handleOpen}
        height=" max-h-[75vh]"
        onClose={() => handleCloseModal(true)}
      >
        <div className="flex flex-col items-center gap-3 py-6 ">
          <img
            src={guest?.avatarUrl}
            alt="Avatar"
            className="w-13 h-13 rounded-full "
          />

          <p className="text-white text-base font-medium">{guest?.name}</p>

          <div className="w-full h-px bg-white/20 my-2.5" />

          {guest?.age && (
            <div className="flex items-center gap-2 mb-2.5 w-full">
              <FaCalendarAlt className="text-white text-[20px]" />
              <p className="text-white text-sm">{guest?.age} years old</p>
            </div>
          )}

          <div className="flex items-center gap-2 mb-2.5 w-full">
            <FaPhoneAlt className="text-white text-[20px]" />
            <p className="text-white text-sm">{guest?.phoneNumber}</p>
          </div>
          {isTicketedEvent && (
            <div className="flex items-center gap-2 mb-2.5 w-full">
              <IoTicket className="text-white text-[20px] rotate-45" />
              <p className="text-white text-sm">{guest?.ticketName}</p>
            </div>
          )}
          {guest?.answersToQuestionnaire &&
            Object.values(guest?.answersToQuestionnaire || {}).filter(
              //check only for non null values
              (answer) => answer !== null,
            ).length > 0 && (
              <div
                className="flex items-center gap-2 mb-2.5 w-full cursor-pointer"
                onClick={onReviewQuestionnaire}
              >
                <IoDocumentText className="text-[#0A84FF] text-[20px]" />
                <p className="text-[#0A84FF] text-sm">
                  View questionnaire answers
                </p>
              </div>
            )}
          <div className=" mt-2.5" />

          {guest?.status === EventStatusType.UNDER_REVIEW && (
            <>
              {!isGuestReviewed && (
                <div className="flex justify-between gap-2 w-full">
                  <button
                    onClick={() => {
                      setCurrentAction(ReviewGuestAction.DISAPPROVE)
                      handleReviewGuest(ReviewGuestAction.DISAPPROVE)
                    }}
                    disabled={isLoading}
                    className={`w-full border-[0.5px] border-white/50 py-3 rounded-3xl flex items-center justify-center bg-transparent ${
                      currentAction === ReviewGuestAction.DISAPPROVE &&
                      isLoading
                        ? "opacity-50 cursor-not-allowed"
                        : isLoading &&
                            !(currentAction === ReviewGuestAction.DISAPPROVE)
                          ? "hidden"
                          : ""
                    }`}
                  >
                    <span className="text-white text-base font-semibold w-full">
                      {currentAction === ReviewGuestAction.DISAPPROVE &&
                      isLoading ? (
                        <span className="flex items-center gap-2 justify-center">
                          <span>Declining</span>
                          <LoadingSpinnerInline size={6} />
                        </span>
                      ) : (
                        "Decline"
                      )}
                    </span>
                  </button>

                  <button
                    disabled={isLoading}
                    onClick={() => {
                      setCurrentAction(ReviewGuestAction.APPROVE)
                      handleReviewGuest(ReviewGuestAction.APPROVE)
                    }}
                    className={`w-full py-3 rounded-3xl bg-white/10 text-white text-base font-semibold flex items-center justify-center ${
                      currentAction === ReviewGuestAction.APPROVE && isLoading
                        ? "opacity-50 cursor-not-allowed"
                        : isLoading &&
                            !(currentAction === ReviewGuestAction.APPROVE)
                          ? "hidden"
                          : ""
                    }`}
                  >
                    {currentAction === ReviewGuestAction.APPROVE &&
                    isLoading ? (
                      <span className="flex items-center gap-2 justify-center">
                        <span>Approving</span>
                        <LoadingSpinnerInline size={6} />
                      </span>
                    ) : (
                      "Approve"
                    )}
                  </button>
                </div>
              )}
              {isGuestReviewed && !isLoading && (
                <div className="flex flex-col gap-2 w-full">
                  <button
                    disabled={false}
                    className={`w-full border-[0.5px] bg-white/10 py-3 rounded-3xl flex items-center justify-center  `}
                  >
                    <span className="text-white/30 text-base font-semibold w-full flex gap-2 items-center justify-center ">
                      {currentAction === ReviewGuestAction.DISAPPROVE
                        ? "Declined"
                        : "Approved"}
                      {currentAction === ReviewGuestAction.APPROVE ? (
                        <FaRegCircleCheck className="text-[#30D158]" />
                      ) : (
                        <FaRegCircleXmark className="text-[#FF3B30]" />
                      )}
                    </span>
                  </button>
                  {currentAction === ReviewGuestAction.APPROVE ? (
                    <p className="flex items-center gap-2 justify-center w-full text-white">
                      Approved — you’ll find them in the Going tab.
                    </p>
                  ) : (
                    <p className="flex items-center gap-2 justify-center w-full text-white">
                      Declined — you’ll find them in the Not Going tab.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          {(guest?.status === EventStatusType.ATTENDING ||
            guest?.status === EventStatusType.ACCEPTED) &&
            isTicketedEvent && (
              <button
                className="w-full  py-3 rounded-3xl bg-white/10 text-white text-base flex items-center justify-center border-white/20 border-0 border-t-[0.7px]"
                onClick={checkInGuest}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2 justify-center">
                    <span>Checking in</span>
                    <LoadingSpinnerInline size={6} />
                  </span>
                ) : isCheckedIn ? (
                  <span className="flex items-center gap-2 justify-center">
                    <span className="opacity-30">Checked in</span>
                    <FaRegCircleCheck className="text-[#30D158]" />
                  </span>
                ) : (
                  "Check in"
                )}
              </button>
            )}
        </div>
      </BottomSheet>
    )
  },
)

export default ReviewGuests
